import React, { useMemo, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import useListKeyboardNavigation from 'hooks/useListKeyboardNavigation';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useDropdownContentSize from 'hooks/useDropdownContentSize';

import './SelectCustomOptions.styles.scss';

export const isMulSelect = (
  property: string[] | string,
): property is string[] => Array.isArray(property);

export type CustomSelectOption<T = string> = {
  value: T;
  renderedOption: React.ReactNode;
};

type Options<T = string | string[]> = {
  options: Array<CustomSelectOption>;
  value: T;
  defaultValue?: T;
  onChange: (val: T) => void;
};

type SelectCustomOptionsProps<T> = {
  className?: string;
  renderedTrigger?: React.ReactNode;
  placeholder?: React.ReactNode;
  renderAsPortal?: boolean;
  hasError?: boolean;
  portalClassName?: string;
} & Options<T>;

function SelectCustomOptions<T extends string | string[]>(
  props: SelectCustomOptionsProps<T>,
) {
  const { t } = useTranslation();
  const {
    options = [],
    className,
    value,
    renderedTrigger,
    renderAsPortal,
    portalClassName,
    hasError,
    placeholder = t('chooseOption'),
    onChange,
  } = props;
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const [trigger, triggerRef] = useCallbackRef();

  const { dropdownContentRef } = useDropdownContentSize(
    dropdown,
    renderAsPortal,
  );

  const classes = classNames(
    'select-custom-options',
    { 'has-error': hasError, 'rendered-as-portal': renderAsPortal },
    className,
  );

  const Trigger = useMemo(() => {
    const renderedValue = isMulSelect(value)
      ? value.length
        ? value.map(
            (val) => options.find((opt) => opt.value === val).renderedOption,
          )
        : placeholder
      : options.find((opt) => opt.value === value)?.renderedOption ||
        placeholder;

    return renderedTrigger || renderedValue;
  }, [options, placeholder, renderedTrigger, value]);

  const onOptionSelect = useCallback(
    (option: CustomSelectOption<string>) => {
      if (isMulSelect(value)) {
        const found = value.find((val) => val === option.value);

        if (found) {
          onChange(value.filter((val) => val !== option.value) as T);
        } else {
          onChange([...value, option.value] as T);
        }
      } else {
        onChange(option.value as T);
        dropdown?.setIsOpen(false);
      }
    },
    [dropdown, onChange, value],
  );

  const { activeIndex, onMouseEnter, onKeyDown } = useListKeyboardNavigation({
    itemsCount: options.length,
    showResults: dropdown?.isOpen,
    onSelect: (index) => onOptionSelect(options[index]),
  });

  //setting element width in case it's a portal (100% does not work then)
  //no dependency array because:
  //since mozzila and safari do not get proper values on initial dependencies
  //this has to run on every change (no dep array)
  useEffect(() => {
    if (!trigger || !renderAsPortal || !dropdownContentRef?.current) return;
    const w = trigger.clientWidth;
    w &&
      dropdownContentRef.current.style.setProperty('--element-width', `${w}px`);
  });

  useEffect(() => {
    if (!dropdown?.isOpen || !dropdownContentRef?.current) return;
    dropdownContentRef.current.focus();
  }, [dropdown?.isOpen, dropdownContentRef]);

  return (
    <Dropdown
      trigger={
        <div
          ref={triggerRef}
          className={classNames('select-custom-options__trigger', {
            miltiple: isMulSelect(value),
          })}
        >
          {Trigger}
          <ChevrondownIcon />
        </div>
      }
      className={classes}
      ref={dropdownRef}
      renderAsPortal={renderAsPortal}
      portalClassName={classNames(
        'select-custom-options-portal',
        portalClassName,
      )}
    >
      <div
        tabIndex={0}
        className="sel-focused-div"
        ref={dropdownContentRef}
        onKeyDownCapture={onKeyDown}
      >
        <ul className="select-custom-options__option-list">
          {options.map((option, i) => (
            <li
              key={option.value}
              className={classNames({
                selected: isMulSelect(value)
                  ? value.includes(option.value)
                  : option.value === value,
                hovered: activeIndex === i,
              })}
              onClick={() => onOptionSelect(option)}
              onMouseEnter={() => onMouseEnter(i)}
            >
              {option.renderedOption}
            </li>
          ))}
        </ul>
      </div>
    </Dropdown>
  );
}

export default SelectCustomOptions;
